"use client";

import { useRealtimeKitMeeting, useRealtimeKitSelector } from "@cloudflare/realtimekit-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Icon } from "@iconify-icon/react";

import Whiteboard from "../components/Whiteboard";
import { AudioParticipant, VideoParticipant } from "../components/MeetingParticipant.tsx";
import MeetingControls from "../components/MeetingControls.tsx";
import ScreenShareView from "../components/ScreenShareView";
import PreJoinUI from "../components/PreJoinUI";
import ClassroomChat from "../components/ClassroomChat";
import { useClassroomStore } from "../store/useClassroomStore";
import ClassroomTabs from "../components/ClassroomTabs";
import VideoSyncView from "../components/VideoSyncView";
import RecordingIndicator from "../components/RecordingIndicator";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Hook to track screen dimensions
const useScreenDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  return dimensions;
};

function ClassroomView() {
  // Device breakpoints based on call grid specifications
  const isMobile = useMediaQuery({ maxWidth: 767 }); // < 768px
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 }); // 768px - 1024px
  const isLaptopDesktop = useMediaQuery({ minWidth: 1025, maxWidth: 1920 }); // 1025px - 1920px
  const isUltraWide = useMediaQuery({ minWidth: 1921 }); // > 1920px
  const isSmallScreen = useMediaQuery({ maxWidth: 1024 }); // Mobile + Tablet

  // Orientation detection
  const isPortrait = useMediaQuery({ orientation: "portrait" });
  const isShort = useMediaQuery({ maxHeight: 767 });

  const { lessonId } = useParams();

  // Track screen dimensions
  const { width: screenWidth, height: screenHeight } = useScreenDimensions();

  // Use Cloudflare Realtime Kit hooks instead of VideoSDK
  const { meeting } = useRealtimeKitMeeting();
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);

  // Get participants directly from Cloudflare selectors
  const self = useRealtimeKitSelector((m) => m.self);
  const remoteParticipants = useRealtimeKitSelector((m) => m.participants.joined.toArray());

  // Create allParticipants array with self + remote participants
  const allParticipants = useMemo(
    () => (self ? [self, ...remoteParticipants] : remoteParticipants),
    [remoteParticipants, self],
  );
  const participantCount = useMemo(() => allParticipants.length ?? 0, [allParticipants]);

  // Only subscribe to the specific store values we need
  const view = useClassroomStore((state) => state.view);
  const isJoined = useClassroomStore((state) => state.isJoined);
  const presenterId = useClassroomStore((state) => state.presenterId);
  const isChatOpen = useClassroomStore((state) => state.isChatOpen);
  const isPipMode = useClassroomStore((state) => state.isPipMode);
  const setIsLoading = useClassroomStore((state) => state.setIsLoading);
  const isWhiteboardFullscreen = useClassroomStore((state) => state.isWhiteboardFullscreen);
  const setIsWhiteboardFullscreen = useClassroomStore((state) => state.setIsWhiteboardFullscreen);
  const showParticipantsOnMobile = useClassroomStore((state) => state.showParticipantsOnMobile);
  const setShowParticipantsOnMobile = useClassroomStore(
    (state) => state.setShowParticipantsOnMobile,
  );
  const setLessonId = useClassroomStore((state) => state.setLessonId);

  // Manage participant visibility based on view
  useEffect(() => {
    if (isMobile) {
      if (view === "call") {
        setShowParticipantsOnMobile(true);
      }
    }
  }, [view, isMobile, setShowParticipantsOnMobile]);

  // Ensure loading is hidden when ClassroomView is rendered and user is joined
  useEffect(() => {
    if (isJoined && meeting) {
      setIsLoading(false);
      setLessonId(lessonId);
    }
  }, [isJoined, meeting, setIsLoading, lessonId, setLessonId]);

  // Clean up when component unmounts (removed AudioManager dependency)
  useEffect(() => {
    return () => {
      // Only clean up if we're not in PiP mode
      const { isPipMode } = useClassroomStore.getState();

      if (!isPipMode) {
        console.log("ClassroomView cleaned up");
      }
    };
  }, []);

  // Check if someone is sharing screen
  const isScreenSharing = presenterId !== null;

  // Get call view grid layout based on call grid specifications
  const getCallGridLayout = () => {
    // Mobile Devices (< 768px)
    if (isMobile || isShort) {
      if (isPortrait) {
        // Portrait orientation
        if (participantCount === 1) {
          return { cols: 1, rows: 1, maxVisible: 1 };
        } else if (participantCount === 2) {
          return { cols: 1, rows: 2, maxVisible: 2 };
        } else if (participantCount === 3) {
          return { cols: 1, rows: 3, maxVisible: 3 };
        } else if (participantCount === 4) {
          return { cols: 1, rows: 4, maxVisible: 4 };
        } else {
          // 5-10: Paginated 2x2 grid
          return { cols: 2, rows: 2, maxVisible: 4, paginated: true };
        }
      } else {
        // Landscape orientation
        if (participantCount === 1) {
          return { cols: 1, rows: 1, maxVisible: 1 };
        } else if (participantCount === 2) {
          return { cols: 2, rows: 1, maxVisible: 2 };
        } else if (participantCount === 3) {
          return { cols: 3, rows: 1, maxVisible: 3 };
        } else if (participantCount === 4) {
          return { cols: 4, rows: 1, maxVisible: 4 };
        } else {
          // 5-10: Scrollable filmstrip
          return { cols: 4, rows: 1, maxVisible: 4, scrollable: true };
        }
      }
    }

    // Tablets (768px - 1024px)
    if (isTablet) {
      if (participantCount <= 2) {
        return { cols: participantCount, rows: 1, maxVisible: participantCount };
      } else if (participantCount <= 4) {
        return { cols: 2, rows: 2, maxVisible: 4 };
      } else if (participantCount <= 6) {
        return { cols: 3, rows: 2, maxVisible: 6 };
      } else if (participantCount <= 9) {
        return { cols: 3, rows: 3, maxVisible: 9 };
      } else {
        return { cols: 4, rows: 3, maxVisible: 10 };
      }
    }

    // Laptops & Desktops (1025px - 1920px)
    if (isLaptopDesktop) {
      if (participantCount === 1) {
        return { cols: 1, rows: 1, maxVisible: 1 };
      } else if (participantCount <= 3) {
        return { cols: participantCount, rows: 1, maxVisible: participantCount };
      } else if (participantCount === 4) {
        return { cols: 2, rows: 2, maxVisible: 4 };
      } else if (participantCount <= 6) {
        return { cols: 3, rows: 2, maxVisible: 6 };
      } else if (participantCount <= 9) {
        return { cols: 3, rows: 3, maxVisible: 9 };
      } else {
        return { cols: 4, rows: 3, maxVisible: 10 };
      }
    }

    // Ultra-Wide Monitors (> 1920px)
    if (isUltraWide) {
      if (participantCount === 1) {
        return { cols: 1, rows: 1, maxVisible: 1 };
      } else if (participantCount <= 5) {
        return { cols: participantCount, rows: 1, maxVisible: participantCount };
      } else {
        return { cols: 5, rows: 2, maxVisible: 10 };
      }
    }

    // Fallback
    return { cols: 1, rows: 1, maxVisible: 1 };
  };

  // Get fullscreen activity layout (whiteboard, screenshare, video)
  const getFullscreenActivityLayout = () => {
    // Check for special case: width >= 800px and height < 500px
    if (screenWidth >= 800 && screenHeight < 500) {
      return { maxVisible: 0, audioOnly: true, layout: "none" }; // Only audio, no video
    }

    if (isMobile) {
      // Mobile: 2 participants in a row, rest as audio
      return { maxVisible: 2, layout: "row" };
    } else {
      // Laptops and bigger: up to 4 participants in a column
      return { maxVisible: 4, layout: "column" };
    }
  };

  const callGridLayout = getCallGridLayout();
  const fullscreenLayout = getFullscreenActivityLayout();

  // Split participants based on the current layout
  const getParticipantSplit = () => {
    if (view === "call") {
      const maxVisible = callGridLayout.maxVisible || participantCount;

      return {
        visibleParticipants: allParticipants.slice(0, maxVisible),
        audioOnlyParticipants: allParticipants.slice(maxVisible),
      };
    } else {
      // Fullscreen activities
      if ("audioOnly" in fullscreenLayout && fullscreenLayout.audioOnly) {
        // Special case: only audio, no video
        return {
          visibleParticipants: [],
          audioOnlyParticipants: allParticipants,
        };
      } else {
        const maxVisible = fullscreenLayout.maxVisible || 0;

        return {
          visibleParticipants: allParticipants.slice(0, maxVisible),
          audioOnlyParticipants: allParticipants.slice(maxVisible),
        };
      }
    }
  };

  const { visibleParticipants } = getParticipantSplit();

  // Determine content width based on chat state - improved for tablets
  const getContentWidth = () => {
    if (isMobile) return "w-full";
    // Reserve space only when chat is open on desktop, otherwise take full width
    if (isChatOpen && !isSmallScreen && !isPipMode) return "w-[75%]";

    return "w-full";
  };

  const contentWidth = getContentWidth();

  // Determine padding based on screen size
  const getPadding = () => {
    if (isMobile) return "px-1 pt-1 pb-1"; // Minimal padding on mobile
    if (isTablet) return "px-2 pt-1 pb-1"; // Reduced padding on tablet

    return "px-4 pt-1 pb-1"; // Normal padding on desktop
  };

  const paddingClass = getPadding();

  // Motion configs (memoized to keep spring consistent)
  const prefersReducedMotion = useReducedMotion();
  const transitions = useMemo(
    () => ({
      layout: prefersReducedMotion
        ? { duration: 0.16 }
        : { type: "spring", stiffness: 360, damping: 32, mass: 0.6 },
      fade: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
      // Crossfade slide for tab switches
      tabEnter: prefersReducedMotion
        ? { duration: 0.18 }
        : { type: "spring", stiffness: 440, damping: 36, mass: 0.5 },
      tabExit: prefersReducedMotion
        ? { duration: 0.16 }
        : { type: "spring", stiffness: 440, damping: 38, mass: 0.5 },
      list: prefersReducedMotion
        ? { duration: 0.14 }
        : { type: "spring", stiffness: 420, damping: 30, mass: 0.45 },
      item: prefersReducedMotion
        ? { duration: 0.14 }
        : { type: "spring", stiffness: 460, damping: 32, mass: 0.42 },
    }),
    [prefersReducedMotion],
  );

  // For participant GRID reflow: disable enter/exit to avoid popping/jumps; only animate layout
  const gridListProps = { initial: false, animate: undefined, exit: undefined };
  const gridItemProps = { initial: false, animate: undefined, exit: undefined };

  // Always render all participant audio
  const renderAllParticipantsAudio = () => {
    return (
      <>
        {allParticipants.map((participant: any) => (
          <AudioParticipant key={`audio-${participant.id}`} participant={participant} />
        ))}
      </>
    );
  };

  // Render video participants based on current layout and view
  const renderVideoParticipants = () => {
    if (view === "call") {
      return renderCallViewParticipants();
    } else {
      return renderFullscreenActivityParticipants();
    }
  };

  // Render call view participants with proper grid layout
  const renderCallViewParticipants = () => {
    const layout = callGridLayout;

    if (isMobile && layout.scrollable) {
      // Mobile landscape with scrollable filmstrip
      return (
        <ScrollArea className="h-full w-full">
          <motion.div
            layout
            className="flex h-full gap-2 p-2"
            {...gridListProps}
            transition={transitions.list}
          >
            {visibleParticipants.map((participant: any) => (
              <motion.div
                key={participant.id}
                layout
                className="h-full min-w-[150px] flex-shrink-0"
                {...gridItemProps}
                transition={transitions.item}
              >
                <VideoParticipant compact={false} participant={participant} />
              </motion.div>
            ))}
          </motion.div>
        </ScrollArea>
      );
    }

    if (isMobile && layout.paginated) {
      // Mobile portrait with paginated 2x2 grid (simplified to show first 4)
      return (
        <motion.div
          layout
          className="grid h-full w-full gap-2 p-2"
          style={{
            gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
          }}
          {...gridListProps}
          transition={transitions.list}
        >
          {visibleParticipants.map((participant: any) => (
            <motion.div
              key={participant.id}
              layout
              className="h-full min-h-0 w-full min-w-0"
              {...gridItemProps}
              transition={transitions.item}
            >
              <VideoParticipant compact={false} participant={participant} />
            </motion.div>
          ))}
        </motion.div>
      );
    }

    // Standard grid layout for all other cases
    return (
      <motion.div
        layout
        className="grid h-full w-full gap-2 p-2"
        style={{
          gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
        }}
        {...gridListProps}
        transition={transitions.list}
      >
        {visibleParticipants.map((participant: any) => (
          <motion.div
            key={participant.id}
            layout
            className="h-full min-h-0 w-full min-w-0"
            {...gridItemProps}
            transition={transitions.item}
          >
            <VideoParticipant compact={false} participant={participant} />
          </motion.div>
        ))}
      </motion.div>
    );
  };

  // Render fullscreen activity participants (sidebar/bottom)
  const renderFullscreenActivityParticipants = () => {
    const layout = fullscreenLayout;

    if (layout.audioOnly) {
      // Special case: only audio, no video
      return null;
    }

    if (layout.layout === "row") {
      // Mobile: 2 participants in a row
      return (
        <motion.div
          layout
          className="flex h-full gap-2 p-2"
          {...gridListProps}
          transition={transitions.list}
        >
          {visibleParticipants.map((participant: any) => (
            <motion.div
              key={participant.id}
              layout
              className="h-full min-w-0 flex-1"
              {...gridItemProps}
              transition={transitions.item}
            >
              <VideoParticipant compact={true} participant={participant} />
            </motion.div>
          ))}
        </motion.div>
      );
    } else {
      // Desktop/laptop: participants in a column
      if (visibleParticipants.length > 4) {
        // Use ScrollArea if more than 4 participants
        return (
          <ScrollArea className="h-full">
            <motion.div
              layout
              className="flex h-full flex-col gap-2 p-2"
              {...gridListProps}
              transition={transitions.list}
            >
              {visibleParticipants.map((participant: any) => (
                <motion.div
                  key={participant.id}
                  layout
                  className="min-h-0 w-full flex-1"
                  {...gridItemProps}
                  transition={transitions.item}
                >
                  <VideoParticipant compact={true} participant={participant} />
                </motion.div>
              ))}
            </motion.div>
          </ScrollArea>
        );
      } else {
        return (
          <motion.div
            layout
            className="flex h-full flex-col gap-2 p-2"
            {...gridListProps}
            transition={transitions.list}
          >
            {visibleParticipants.map((participant: any) => (
              <motion.div
                key={participant.id}
                layout
                className="min-h-0 w-full flex-1"
                {...gridItemProps}
                transition={transitions.item}
              >
                <VideoParticipant compact={true} participant={participant} />
              </motion.div>
            ))}
          </motion.div>
        );
      }
    }
  };

  return (
    <div className={`bg-background flex h-full max-h-[100dvh] w-full flex-col overflow-hidden`}>
      {/* Always render all participant audio */}
      {renderAllParticipantsAudio()}

      {roomState === "joined" && (
        <div className="flex h-full flex-col">
          <div className={`relative flex-grow overflow-hidden ${paddingClass}`}>
            <div className="flex h-full flex-col">
              {/* Show tabs for all screen sizes - they now include mobile controls */}
              <ClassroomTabs isScreenSharing={isScreenSharing} />

              {/* Recording Indicator */}
              <AnimatePresence initial={false}>
                <motion.div
                  key={`rec-indicator-${String(useClassroomStore.getState().isRecording)}`}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-4 right-4 z-50"
                  exit={{ opacity: 0, y: -8 }}
                  initial={{ opacity: 0, y: -8 }}
                  transition={transitions.fade}
                >
                  <RecordingIndicator />
                </motion.div>
              </AnimatePresence>

              {/* Container with persistent border - add recording indicator */}
              <div
                className={cn(
                  "flex h-full overflow-hidden",
                  !isMobile ? "rounded-tr-xl rounded-b-xl" : "rounded-xl",
                )}
              >
                {/* Main content area - split between call view and fullscreen activities */}
                <div className={`relative h-full ${contentWidth}`}>
                  {view === "call" ? (
                    /* Call view - separate from fullscreen activities */
                    <AnimatePresence mode="wait">
                      <motion.div
                        key="call"
                        layout
                        animate={{ opacity: 1, x: 0 }}
                        className="h-full w-full"
                        exit={{ opacity: 0, x: -14 }}
                        initial={{ opacity: 0, x: 14 }}
                        transition={{ layout: transitions.layout, ...transitions.tabEnter }}
                      >
                        <div className="h-full w-full">
                          {/* Call view with new grid layout */}
                          {renderVideoParticipants()}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  ) : (
                    /* Fullscreen activities with persistent participants */
                    <div className={`flex ${isMobile ? "flex-col" : "flex-row"} h-full w-full`}>
                      {/* Main activity area - only this part animates */}
                      <div
                        className={`${
                          isMobile
                            ? showParticipantsOnMobile
                              ? isPortrait
                                ? "h-[calc(100%-80px)] w-full"
                                : "h-[calc(100%-80px)] w-full"
                              : "h-full w-full"
                            : isTablet && !isShort
                              ? "w-[85%]"
                              : !isShort
                                ? "w-[80%]"
                                : "w-full"
                        }`}
                      >
                        <AnimatePresence mode="wait">
                          {view === "whiteboard" && (
                            <motion.div
                              key="whiteboard"
                              animate={{ opacity: 1, x: 0 }}
                              className="h-full w-full"
                              exit={{ opacity: 0, x: -14 }}
                              initial={{ opacity: 0, x: 14 }}
                              transition={transitions.tabEnter}
                            >
                              <Whiteboard />
                            </motion.div>
                          )}

                          {view === "screenshare" && isScreenSharing && (
                            <motion.div
                              key="screenshare"
                              animate={{ opacity: 1, x: 0 }}
                              className="h-full w-full"
                              exit={{ opacity: 0, x: -14 }}
                              initial={{ opacity: 0, x: 14 }}
                              transition={transitions.tabEnter}
                            >
                              <ScreenShareView
                                participant={allParticipants.find((p: any) => p.id === presenterId)}
                              />
                            </motion.div>
                          )}

                          {view === "video" && (
                            <motion.div
                              key="video"
                              animate={{ opacity: 1, x: 0 }}
                              className="h-full w-full"
                              exit={{ opacity: 0, x: -14 }}
                              initial={{ opacity: 0, x: 14 }}
                              transition={transitions.tabEnter}
                            >
                              <VideoSyncView compact={false} />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Persistent participants column for desktop - only shows for fullscreen activities */}
                      {!isMobile && !isShort && (
                        <motion.div
                          layout
                          className={`border-border ${isTablet ? "w-[15%]" : "w-[20%]"} border-l`}
                          transition={transitions.layout}
                        >
                          {renderFullscreenActivityParticipants()}
                        </motion.div>
                      )}

                      {/* Persistent mobile participants row - with visibility control */}
                      <AnimatePresence initial={false}>
                        {isMobile && showParticipantsOnMobile && (
                          <motion.div
                            key="mobile-participants"
                            animate={{ opacity: 1, y: 0 }}
                            className="w-full"
                            exit={{ opacity: 0, y: 8 }}
                            initial={{ opacity: 0, y: 8 }}
                            style={{ height: "80px" }}
                            transition={transitions.list}
                          >
                            {renderFullscreenActivityParticipants()}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* Chat sidebar - always mounted for hook persistence; only revealed when open */}
                {!isPipMode && !isSmallScreen && (
                  <motion.div
                    key="chat-desktop"
                    layout
                    animate={{
                      width: isChatOpen ? "25%" : 0,
                      opacity: isChatOpen ? 1 : 0,
                    }}
                    className="ms-2 h-full"
                    initial={false}
                    style={{ overflow: "hidden" }}
                    transition={{ ...transitions.layout, opacity: transitions.fade }}
                  >
                    {/* Keep component mounted to preserve useChat state */}
                    <div className={cn("h-full", isChatOpen ? "w-full" : "w-0")}>
                      <ClassroomChat isMobile={false} />
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
          {/* Controls - non-absolute, centered at the bottom */}
          {!isMobile && (
            <div className={`flex items-center justify-center ${isTablet ? "pb-2" : "pb-4"}`}>
              <MeetingControls />
            </div>
          )}
          {/* Mobile/Tablet Chat Dialog - now includes tablets */}
          {isSmallScreen && !isPipMode && <ClassroomChat isMobile={true} />}
          {/* Fullscreen Whiteboard Modal */}
          <Dialog open={isWhiteboardFullscreen} onOpenChange={setIsWhiteboardFullscreen}>
            <DialogContent className="m-0 h-dvh w-screen max-w-none rounded-none p-0">
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="relative h-full w-full"
                exit={{ opacity: 0, scale: 0.98 }}
                initial={{ opacity: 0, scale: 0.98 }}
                transition={transitions.list}
              >
                <Whiteboard fullscreen={true} />
                <Button
                  className="absolute top-4 right-4 z-50"
                  size="icon"
                  variant="secondary"
                  onClick={() => setIsWhiteboardFullscreen(false)}
                >
                  <Icon height="20" icon="lucide:x" width="20" />
                </Button>
              </motion.div>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {roomState === "init" && <PreJoinUI />}
    </div>
  );
}

export default ClassroomView;
